import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { BarChart3, DollarSign, Layers, Users } from 'lucide-react';
import { parseAsArrayOf, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useMemo } from 'react';
import { formatToCompactCurrency } from 'src/common/utils/format';
import { useProjectsContext } from 'src/modules/projects/context';

import { StatCard } from '@/components';
import { FilterToolbar } from '@/components/composed';
import { type ComboboxOption, Skeleton, Text } from '@/design-system/components/ui';
import { CHART_COLORS } from '@/design-system/tokens';

import { tiersService } from '../../tiers/infra/tiersService';
import {
  type BreakdownGroupByDimension,
  GROUP_BY_OPTIONS,
  useAffiliateBreakdownFiltersSchema,
} from '../affiliateBreakdownFiltersSchema';
import { type DateRangeParams, filterTokenEarnings } from '../domain/types';
import { useAffiliateGlobalBreakdown } from '../hooks/useAffiliateGlobalBreakdown';
import { BreakdownChart } from './BreakdownChart';
import { BreakdownTable } from './BreakdownTable';
import { MultiCurrencyEarnings } from './MultiCurrencyEarnings';

dayjs.extend(utc);

const GROUP_BY_VALUES = ['region', 'status', 'tier'] as const satisfies readonly BreakdownGroupByDimension[];

const toApiDate = (value: string): string => (value ? dayjs.utc(value).format('YYYY-MM-DD') : '');

export function BreakdownPage() {
  const { project } = useProjectsContext();
  const projectId = project?.id ?? '';

  const [filters] = useQueryStates({
    groupBy: parseAsStringLiteral(GROUP_BY_VALUES).withDefault('region'),
    statuses: parseAsArrayOf(parseAsString).withDefault([]),
    regions: parseAsArrayOf(parseAsString).withDefault([]),
    tiers: parseAsArrayOf(parseAsString).withDefault([]),
    dateFrom: parseAsString.withDefault(''),
    dateTo: parseAsString.withDefault(''),
  });

  const apiDateParams = useMemo<DateRangeParams>(() => {
    if (!filters.dateFrom || !filters.dateTo) return {};
    return { dateRange: 'custom', dateFrom: toApiDate(filters.dateFrom), dateTo: toApiDate(filters.dateTo) };
  }, [filters.dateFrom, filters.dateTo]);

  const { data: tiersList } = useQuery({
    queryKey: ['project-tiers', projectId],
    queryFn: () => tiersService.list(projectId),
    enabled: !!projectId,
  });

  const tierOptions: ComboboxOption[] = useMemo(
    () => (tiersList?.tiers ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tiersList?.tiers],
  );

  const filtersSchema = useAffiliateBreakdownFiltersSchema({
    tierOptions,
    groupBy: filters.groupBy,
  });

  const { breakdown, isLoading } = useAffiliateGlobalBreakdown({
    projectId,
    groupBy: filters.groupBy,
    ...apiDateParams,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    regions: filters.regions.length > 0 ? filters.regions : undefined,
    tiers: filters.tiers.length > 0 ? filters.tiers : undefined,
  });

  const results = breakdown?.results ?? [];

  const groupLabel = GROUP_BY_OPTIONS.find((o) => o.value === filters.groupBy)?.label ?? 'Groups';

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div>
        <Text variant="display-sm" as="h3" className="tw:m-0">
          Breakdown
        </Text>
        <Text variant="body-md" color="muted">
          Aggregate affiliate metrics by dimension
        </Text>
      </div>

      <FilterToolbar schema={filtersSchema} />

      <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2 tw:lg:grid-cols-4">
        <StatCard
          icon={<Layers size={24} color={CHART_COLORS.primary.dark} />}
          label={`Total ${groupLabel}`}
          value={isLoading ? <Skeleton width="60px" height="32px" /> : (breakdown?.totalGroups ?? 0)}
        />
        <StatCard
          icon={<BarChart3 size={24} color={CHART_COLORS.primary.dark} />}
          label="Total Volume"
          value={
            isLoading ? <Skeleton width="80px" height="32px" /> : formatToCompactCurrency(breakdown?.totalVolume ?? 0)
          }
        />
        <StatCard
          icon={<DollarSign size={24} color={CHART_COLORS.primary.dark} />}
          label="Total Revenue"
          value={
            isLoading ? <Skeleton width="80px" height="32px" /> : formatToCompactCurrency(breakdown?.totalRevenue ?? 0)
          }
        />
        <StatCard icon={<Users size={24} color={CHART_COLORS.primary.dark} />} label="Total Earnings">
          {isLoading ? (
            <Skeleton width="80px" height="32px" />
          ) : (
            <MultiCurrencyEarnings
              earnings={filterTokenEarnings(breakdown?.totalEarnings ?? [])}
              size="lg"
              scaleByDecimals
            />
          )}
        </StatCard>
      </div>

      <BreakdownChart results={results} isLoading={isLoading} />

      <BreakdownTable results={results} isLoading={isLoading} />
    </div>
  );
}
